import apiService from './api';

export interface Invoice {
  _id?: string; // Mongoose _id field
  id?: string; // Mongoose virtual id field
  invoiceNumber: string;
  dealerId: string;
  invoiceType: 'activation_fee' | 'deposit_credits';
  netAmount: number;
  vatRate: number;
  vatAmount: number;
  grossAmount: number;
  currency: string;
  positionDescription: string;
  quantity: number;
  invoiceDate: string;
  pdfFilePath?: string;
  pdfUrl?: string;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceListResponse {
  success: boolean;
  data: Invoice[];
  message: string;
}

export interface InvoiceResponse {
  success: boolean;
  data: Invoice;
  message: string;
}

export class InvoiceService {
  /**
   * Get all invoices for current dealer
   * @param type Optional filter by invoice type
   */
  async getInvoices(type?: 'activation_fee' | 'deposit_credits'): Promise<Invoice[]> {
    const params = type ? { type } : {};
    const response = await apiService.get<InvoiceListResponse>('/dealer-receipts/invoices', params);
    return response.data;
  }

  /**
   * Get invoice by ID
   * @param invoiceId Invoice ID
   */
  async getInvoiceById(invoiceId: string): Promise<Invoice> {
    const response = await apiService.get<InvoiceResponse>(`/dealer-receipts/invoices/${invoiceId}`);
    return response.data;
  }

  /**
   * Get invoice by deposit ID
   * @param depositId Deposit ID
   */
  async getInvoiceByDepositId(depositId: string): Promise<Invoice> {
    const response = await apiService.get<InvoiceResponse>(`/dealer-receipts/invoices/deposit/${depositId}`);
    return response.data;
  }

  /**
   * Get invoice by invoice number
   * @param invoiceNumber Invoice number (e.g., "INV-2025-0001")
   */
  async getInvoiceByNumber(invoiceNumber: string): Promise<Invoice> {
    const response = await apiService.get<InvoiceResponse>(`/dealer-receipts/invoices/number/${invoiceNumber}`);
    return response.data;
  }

  /**
   * Get invoice PDF download URL
   * @param invoiceId Invoice ID
   * @returns Signed URL for PDF download
   */
  async getInvoicePdfUrl(invoiceId: string): Promise<string> {
    const response = await apiService.get<{
      success: boolean;
      data: { pdfUrl: string; invoiceNumber: string; expiresIn: string };
      message: string
    }>(`/dealer-receipts/invoices/${invoiceId}/pdf-url`);
    return response.data.pdfUrl;
  }

  /**
   * Download invoice PDF
   * Opens PDF in new tab/downloads it
   * @param invoiceId Invoice ID
   */
  async downloadInvoice(invoiceId: string): Promise<void> {
    const baseUrl = apiService.getBaseUrl();
    const token = localStorage.getItem('token');

    // Construct download URL with auth token
    const downloadUrl = `${baseUrl}/dealer-receipts/invoices/${invoiceId}/download`;

    // Open in new window/tab (browser will handle download)
    window.open(`${downloadUrl}?token=${token}`, '_blank');
  }

  /**
   * Download invoice by deposit ID
   * @param depositId Deposit ID
   */
  async downloadInvoiceByDepositId(depositId: string): Promise<void> {
    const baseUrl = apiService.getBaseUrl();
    const token = localStorage.getItem('token');

    const downloadUrl = `${baseUrl}/dealer-receipts/invoices/deposit/${depositId}/download`;
    window.open(`${downloadUrl}?token=${token}`, '_blank');
  }
}

export const invoiceService = new InvoiceService();
export default invoiceService;
